<template>
  <Button
    :label="languageStore.t('auth.login.forgotPassword')"
    variant="link"
    @click="() => (visible = true)"
    style="padding: 0"
  />

  <Dialog
    v-model:visible="visible"
    modal
    :header="languageStore.t('auth.login.forgotPassword')"
    class="w-[600px]"
  >
    <form @submit.prevent="handleSubmit" class="flex flex-col gap-6">
      <span class="text-surface-500 dark:text-surface-400 block">
        {{ languageStore.t('auth.requiredChangePassword.instructions') }}
      </span>
      <div class="flex flex-col gap-2">
        <label for="email" class="font-semibold">
          {{ languageStore.t('auth.requiredChangePassword.emailLabel') }}
        </label>
        <InputText
          id="email"
          v-model="form.email"
          :class="{ 'p-invalid': errors.email }"
          class="flex-auto"
        />
        <small v-if="errors.email" class="text-red-500">{{ errors.email }}</small>
      </div>
      <div class="flex flex-col gap-2">
        <label for="expired-password" class="font-semibold">
          {{ languageStore.t('auth.requiredChangePassword.expiredPasswordLabel') }}
        </label>
        <Password
          toggleMask
          fluid
          v-model="form.expiredPassword"
          :invalid="!!errors.expiredPassword"
        />
        <small v-if="errors.expiredPassword" class="text-red-500">
          {{ errors.expiredPassword }}
        </small>
      </div>
      <div class="flex flex-col gap-2">
        <label for="new-password" class="font-semibold">
          {{ languageStore.t('auth.requiredChangePassword.newPasswordLabel') }}
        </label>
        <small class="text-surface-500 dark:text-surface-400">
          {{ languageStore.t('auth.requiredChangePassword.passwordRequirements') }}
        </small>
        <Password toggleMask fluid v-model="form.newPassword" :invalid="!!errors.newPassword" />
        <small v-if="errors.newPassword" class="text-red-500">{{ errors.newPassword }}</small>
      </div>
      <div class="flex justify-end gap-2">
        <Button
          type="button"
          label="Cancelar"
          severity="secondary"
          @click="visible = false"
        ></Button>
        <Button type="submit" label="Salvar" :loading="isLoading"></Button>
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
import * as userService from '@/services/userService'
import { useLanguageStore } from '@/stores/LanguageStore'
import { Button, Dialog, InputText, Password, useToast } from 'primevue'
import { reactive, ref } from 'vue'
import { z } from 'zod'

const toast = useToast()
const languageStore = useLanguageStore()
const visible = ref(false)
const isLoading = ref(false)

const form = reactive({
  email: '',
  expiredPassword: '',
  newPassword: '',
})

const errors = reactive({
  email: '',
  expiredPassword: '',
  newPassword: '',
})

const schema = z.object({
  email: z.string().email(languageStore.t('auth.requiredChangePassword.validation.emailRequired')),
  expiredPassword: z
    .string()
    .min(1, languageStore.t('auth.requiredChangePassword.validation.expiredPasswordRequired')),
  newPassword: z
    .string()
    .min(8, languageStore.t('auth.requiredChangePassword.validation.minLength'))
    .regex(/[A-Z]/, languageStore.t('auth.requiredChangePassword.validation.newPasswordUppercase'))
    .regex(/[a-z]/, languageStore.t('auth.requiredChangePassword.validation.newPasswordLowercase'))
    .regex(/[0-9]/, languageStore.t('auth.requiredChangePassword.validation.newPasswordNumber'))
    .regex(
      /[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/,
      languageStore.t('auth.requiredChangePassword.validation.newPasswordSpecial'),
    ),
})

const validateForm = () => {
  try {
    schema.parse(form)
    return true
  } catch (error) {
    if (error instanceof z.ZodError) {
      errors.email = error.errors.find((e) => e.path[0] === 'email')?.message || ''
      errors.expiredPassword =
        error.errors.find((e) => e.path[0] === 'expiredPassword')?.message || ''
      errors.newPassword = error.errors.find((e) => e.path[0] === 'newPassword')?.message || ''
    }
    return false
  }
}

const cleanForm = () => {
  form.email = ''
  form.expiredPassword = ''
  form.newPassword = ''
  errors.email = ''
  errors.expiredPassword = ''
  errors.newPassword = ''
}

const handleSubmit = async () => {
  if (!validateForm()) return

  isLoading.value = true
  userService
    .recoverPassword(form.email, form.expiredPassword, form.newPassword)
    .then(() => {
      visible.value = false

      toast.add({
        summary: 'Senha Atualizada',
        detail: 'Faça login com seu e-mail e nova senha',
        severity: 'success',
        life: 3000,
      })

      cleanForm()
    })
    .catch((error) => {
      console.error('Error recovering password:', error)

      const errorMessage = (() => {
        if (error.response?.data?.detail) {
          return error.response.data?.detail
        }
        return 'Erro ao recuperar senha'
      })()

      toast.add({
        summary: 'Erro',
        detail: errorMessage,
        severity: 'error',
        life: 3000,
      })
    })
    .finally(() => {
      isLoading.value = false
    })
}
</script>
